/**
 * @module components/ACS/ACSSessionView
 * @description Vista principal de sesión ACS (Asistente Conversacional Supervisado).
 *
 * Layout de dos columnas durante una sesión activa:
 * - **Izquierda**: iframe de Jitsi Meet para la videollamada
 * - **Derecha**: panel de chat IA con el asistente normativo y lista de propuestas
 *
 * ## Ciclo de vida de la sesión
 * 1. CISO abre la sesión — Jitsi se inicializa, asistente carga el contexto normativo
 * 2. Durante la sesión: el asistente responde preguntas con citas verificadas de la normativa
 * 3. Al finalizar: Gemini genera las minutas, Zod las valida, el CISO las revisa
 * 4. Las tareas propuestas se envían a `ACSProposalReview` para aprobación del CISO
 *
 * @see ACSContext para el estado global de la sesión ACS
 * @see ACSMinutesPanel para la visualización post-sesión de las minutas
 * @see acsAI.ts para el motor IA con guardrails de citas
 */
// ============================================================
// ACSSessionView — Vista principal de sesión ACS
// Layout 2 columnas: Jitsi iframe | Chat IA + Propuestas
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  X, Send, AlertCircle, CheckCircle, Paperclip,
  FileText, Plus, Trash2, StopCircle, BookOpen, ListTodo, CheckCircle2,
  Mic, MicOff, Phone, PhoneOff,
} from 'lucide-react';
import { useACS } from '../../contexts/ACSContext';
import { useAuth } from '../../contexts/AuthContext';
import { ACSMinutesPanel } from './ACSMinutesPanel';
import { ACSAvatar } from './ACSAvatar';
import { loadACSPersonaConfig } from './ACSConfigPanel';
import type { ACSProposedTaskData, ACSSessionMessage } from '../../types/acs';

interface ACSSessionViewProps {
  onClose?: () => void;
}

type SessionTab = 'chat' | 'proposals';

// ──────────────────────────────────────────────────────────
// COMPONENTE DE MENSAJE
// ──────────────────────────────────────────────────────────

interface MessageBubbleProps {
  msg: ACSSessionMessage;
  assistantName: string;
  avatarColor: string;
  avatarInitials: string;
}

function MessageBubble({ msg, assistantName, avatarColor, avatarInitials }: MessageBubbleProps) {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 gap-2`}>
      {/* Avatar asistente */}
      {!isUser && (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold shadow mt-0.5"
          style={{ backgroundColor: avatarColor }}
          title={assistantName}
        >
          {avatarInitials.slice(0, 3) || 'ACS'}
        </div>
      )}

      <div className={`max-w-[80%] ${isUser ? '' : ''}`}>
        {!isUser && (
          <p className="text-[10px] font-semibold mb-0.5" style={{ color: avatarColor }}>
            {assistantName}
          </p>
        )}
        <div className={`rounded-xl px-3.5 py-2.5 text-sm ${
          isUser
            ? 'bg-violet-600 text-white'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
        }`}>
          <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>

          {/* Citas */}
          {msg.citations && msg.citations.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/20 dark:border-gray-600 space-y-1">
              {msg.citations.map((c, i) => (
                <div key={i} className="text-[11px] opacity-80 flex items-start gap-1">
                  <BookOpen className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>{c.sectionRef ?? `doc:${c.docId.slice(0, 8)}`}{c.quote ? ` — "${c.quote.slice(0, 80)}..."` : ''}</span>
                </div>
              ))}
            </div>
          )}

          {/* Indicador guardrail */}
          {msg.citationsValid === false && (
            <div className="mt-2 flex items-center gap-1 text-[11px] text-yellow-300">
              <AlertCircle className="w-3 h-3" />
              <span>Sin citas verificadas en normativa cargada</span>
            </div>
          )}

          <p className={`text-[10px] mt-1 ${isUser ? 'text-violet-200' : 'text-gray-400 dark:text-gray-500'}`}>
            {new Date(msg.createdAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ──────────────────────────────────────────────────────────

export function ACSSessionView({ onClose }: ACSSessionViewProps) {
  const {
    activeSession,
    sessionMessages,
    sendSessionMessage,
    endSession,
    generateMinutes,
    submitProposals,
    closeSession,
  } = useACS();
  const { user } = useAuth();

  // Persona config: cargada una sola vez al montar la vista (no en cada mensaje)
  const persona = loadACSPersonaConfig(user?.organization_id ?? 'demo');

  // Cierre limpio: cancela TTS + limpia contexto + notifica al padre
  function handleClose() {
    window.speechSynthesis?.cancel();
    closeSession();
    onClose?.();
  }

  const [activeTab,      setActiveTab]      = useState<SessionTab>('chat');
  const [inputText,      setInputText]      = useState('');
  const [isSending,      setIsSending]      = useState(false);
  const [isEnding,       setIsEnding]       = useState(false);
  const [isGenerating,   setIsGenerating]   = useState(false);
  const [isSpeaking,     setIsSpeaking]     = useState(false);
  const [minutes,        setMinutes]        = useState<Awaited<ReturnType<typeof generateMinutes>> | null>(null);
  const [showMinutes,    setShowMinutes]    = useState(false);
  const [sendError,      setSendError]      = useState<string | null>(null);
  const [proposalMsg,    setProposalMsg]    = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  // Propuestas locales (editables antes de enviar al CISO)
  const [localProposals,    setLocalProposals]    = useState<ACSProposedTaskData[]>([]);
  const [newProposalTitle,  setNewProposalTitle]  = useState('');

  const messagesEndRef     = useRef<HTMLDivElement>(null);
  const lastSpokenMsgRef   = useRef<string | null>(null);
  const introSpokenRef     = useRef(false);
  // STT refs
  const recognitionRef     = useRef<SpeechRecognition | null>(null);
  const shouldListenRef    = useRef(false);
  const SpeechAPIRef       = useRef<(new () => SpeechRecognition) | null>(null);
  const accumulatedTextRef = useRef('');
  // Voice conversation mode refs
  const voiceModeRef       = useRef(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sendFromVoiceRef   = useRef<() => Promise<void>>(async () => {});
  const startVoiceMicRef   = useRef<() => void>(() => {});

  const [voiceMode, setVoiceMode] = useState(false);

  // Resetear estado de UI al cambiar de sesión.
  // El componente en MainLayout nunca se desmonta (retorna null), por lo que
  // showMinutes/minutes/etc. persisten si no se limpian explícitamente.
  useEffect(() => {
    setShowMinutes(false);
    setMinutes(null);
    setIsEnding(false);
    setIsGenerating(false);
    setInputText('');
    setSendError(null);
    setLocalProposals([]);
    setActiveTab('chat');
    lastSpokenMsgRef.current    = null;
    introSpokenRef.current      = false;
    accumulatedTextRef.current  = '';
    shouldListenRef.current     = false;
    voiceModeRef.current        = false;
    setVoiceMode(false);
  }, [activeSession?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [sessionMessages]);

  // Limpiar mensaje de propuestas tras 3 s
  useEffect(() => {
    if (!proposalMsg) return;
    const t = setTimeout(() => setProposalMsg(null), 3000);
    return () => clearTimeout(t);
  }, [proposalMsg]);

  // TTS — leer en voz alta cada nueva respuesta del asistente
  useEffect(() => {
    const lastMsg = sessionMessages[sessionMessages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return;
    if (lastMsg.id === lastSpokenMsgRef.current) return;
    lastSpokenMsgRef.current = lastMsg.id;

    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();

    const utterance      = new SpeechSynthesisUtterance(lastMsg.content);
    utterance.lang       = 'es-CL';
    utterance.rate       = 0.95;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend   = () => {
      setIsSpeaking(false);
      if (voiceModeRef.current) setTimeout(() => startVoiceMicRef.current(), 400);
    };
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  }, [sessionMessages]);

  // Cancelar TTS al desmontar la vista
  useEffect(() => () => { window.speechSynthesis?.cancel(); }, []);

  // Texto de presentación del asistente (se muestra y lee al abrir una sesión nueva)
  const introText = [
    `Hola, soy ${persona.assistantName}, tu asistente especializado en cumplimiento normativo ISO 27001/27002 y Ley Marco de Ciberseguridad.`,
    `Durante esta sesión puedo ayudarte a consultar controles con citas verificadas, evaluar el estado de cumplimiento de tu organización y proponer acciones correctivas.`,
    `Al finalizar, genero automáticamente una minuta con los puntos tratados. También puedes crear propuestas de acuerdos que serán enviadas al CISO para revisión; una vez aprobadas, quedan registradas en el marco de seguimiento de compromisos de tu organización.`,
    `¿En qué puedo ayudarte hoy?`,
  ].join('\n\n');

  // TTS de bienvenida — se dispara una sola vez cuando la sesión abre sin mensajes previos
  useEffect(() => {
    if (!activeSession) return;               // no hay sesión activa — no hablar
    if (sessionMessages.length > 0) return;   // sesión retomada — no repetir
    if (introSpokenRef.current) return;        // ya leído en esta montadura
    introSpokenRef.current = true;
    if (!('speechSynthesis' in window)) return;
    const utt   = new SpeechSynthesisUtterance(introText);
    utt.lang    = 'es-CL';
    utt.rate    = 0.95;
    utt.onstart = () => setIsSpeaking(true);
    utt.onend   = () => {
      setIsSpeaking(false);
      if (voiceModeRef.current) setTimeout(() => startVoiceMicRef.current(), 400);
    };
    utt.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dictado por voz (Speech-to-Text) ──────────────────
  const [isListening, setIsListening] = useState(false);

  // Inicia una micro-sesión de reconocimiento; se relanza sola en onend mientras
  // shouldListenRef sea true. Esto evita el bug de Chrome donde no-speech + onend
  // mata el reconocimiento aunque continuous sea true.
  function startSession() {
    if (!SpeechAPIRef.current || !shouldListenRef.current) return;

    const rec        = new SpeechAPIRef.current();
    rec.lang           = 'es-ES';
    rec.continuous     = false;   // false es más fiable; reiniciamos manualmente
    rec.interimResults = true;

    let sessionFinal = ''; // texto finalizado en ESTA micro-sesión

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let final = '', interim = '';
      for (let i = 0; i < e.results.length; i++) {
        if (e.results[i].isFinal) final  += e.results[i][0].transcript + ' ';
        else                       interim = e.results[i][0].transcript;
      }
      sessionFinal = final;
      setInputText((accumulatedTextRef.current + final + interim).trim());
    };

    rec.onend = () => {
      // Confirmar texto final de la sesión en el acumulador
      if (sessionFinal) {
        accumulatedTextRef.current = (accumulatedTextRef.current + sessionFinal).trimEnd();
        if (!accumulatedTextRef.current.endsWith(' ')) accumulatedTextRef.current += ' ';
      }
      // Reiniciar si el usuario sigue queriendo escuchar
      if (shouldListenRef.current) {
        setTimeout(startSession, 150);
      } else {
        setIsListening(false);
        recognitionRef.current = null;
      }
    };

    rec.onerror = (e: SpeechRecognitionErrorEvent) => {
      if (e.error === 'aborted') return; // onend se encarga
      if (e.error === 'no-speech') {
        // En modo voz: si hay texto acumulado, auto-enviar al detectar silencio
        if (voiceModeRef.current && accumulatedTextRef.current.trim()) {
          shouldListenRef.current = false; // onend no reiniciará
          void sendFromVoiceRef.current();
        }
        return;
      }
      console.error('[ACS STT]', e.error);
      shouldListenRef.current = false;
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { /* onend reintentará si shouldListenRef sigue true */ }
  }

  function toggleListening() {
    if (shouldListenRef.current) {
      shouldListenRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    const API: (new () => SpeechRecognition) | undefined =
      w.webkitSpeechRecognition || w.SpeechRecognition;
    if (!API) { console.warn('[ACS STT] No disponible'); return; }

    if (isSpeaking) { window.speechSynthesis?.cancel(); setIsSpeaking(false); }

    SpeechAPIRef.current = API;
    // Arrancar acumulador desde lo que ya hay en el textarea (si el usuario escribió algo antes)
    accumulatedTextRef.current = inputText.trimEnd();
    if (accumulatedTextRef.current && !accumulatedTextRef.current.endsWith(' '))
      accumulatedTextRef.current += ' ';

    shouldListenRef.current = true;
    setIsListening(true);
    startSession();
  }

  // Detener reconocimiento al desmontar
  useEffect(() => () => { shouldListenRef.current = false; recognitionRef.current?.stop(); }, []);

  // ── Funciones estables para modo voz (se actualizan cada render via ref) ──

  // Enviar el texto acumulado como mensaje de usuario (usado desde callbacks de STT)
  sendFromVoiceRef.current = async () => {
    const text = accumulatedTextRef.current.trim();
    if (!text || !activeSession) return;
    setInputText('');
    accumulatedTextRef.current = '';
    setSendError(null);
    setIsSending(true);
    setIsListening(false);
    try {
      await sendSessionMessage(activeSession.id, text);
    } catch (e: unknown) {
      setSendError(e instanceof Error ? e.message : 'Error enviando mensaje');
      // Error grave → salir de modo voz para no quedar en loop
      voiceModeRef.current = false;
      setVoiceMode(false);
    } finally {
      setIsSending(false);
    }
  };

  // Reiniciar escucha del micrófono (usado desde TTS onend en modo voz)
  startVoiceMicRef.current = () => {
    if (!voiceModeRef.current) return;
    const w = window as unknown as Record<string, unknown>;
    const API = (w['webkitSpeechRecognition'] ?? w['SpeechRecognition']) as (new () => SpeechRecognition) | undefined;
    if (!API) return;
    SpeechAPIRef.current = API;
    accumulatedTextRef.current = '';
    setInputText('');
    shouldListenRef.current = true;
    setIsListening(true);
    startSession();
  };

  // Activar / desactivar modo de conversación voz↔voz
  function toggleVoiceMode() {
    if (voiceModeRef.current) {
      // Apagar modo voz
      voiceModeRef.current = false;
      setVoiceMode(false);
      shouldListenRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      window.speechSynthesis?.cancel();
      setIsSpeaking(false);
    } else {
      // Encender modo voz — cancelar TTS si estaba sonando y abrir mic
      voiceModeRef.current = true;
      setVoiceMode(true);
      if (isSpeaking) { window.speechSynthesis?.cancel(); setIsSpeaking(false); }
      startVoiceMicRef.current();
    }
  }

  if (!activeSession) return null;

  async function handleSendMessage() {
    if (!inputText.trim() || isSending || !activeSession) return;
    const msg = inputText.trim();
    setInputText('');
    accumulatedTextRef.current = '';
    setSendError(null);
    setIsSending(true);
    try {
      await sendSessionMessage(activeSession.id, msg);
    } catch (e: unknown) {
      setSendError(e instanceof Error ? e.message : 'Error enviando mensaje');
    } finally {
      setIsSending(false);
    }
  }

  async function handleEndSession() {
    if (!activeSession || isEnding) return;
    setIsEnding(true);
    setIsGenerating(false);
    try {
      await endSession(activeSession.id);
      // Generar minuta automáticamente al terminar
      setIsGenerating(true);
      const m = await generateMinutes(activeSession.id);
      setMinutes(m);
      setShowMinutes(true);
    } catch (e: unknown) {
      console.error('[ACS] Error terminando sesión:', e);
      // Si algo falla, cerrar limpiamente para no dejar al usuario atrapado
      handleClose();
    } finally {
      setIsGenerating(false);
      setIsEnding(false);
    }
  }

  async function handleSubmitProposals(proposalsToSubmit: ACSProposedTaskData[]) {
    if (!activeSession || proposalsToSubmit.length === 0) return;
    try {
      await submitProposals(activeSession.id, proposalsToSubmit);
      setLocalProposals([]);
      setProposalMsg({ type: 'ok', text: `${proposalsToSubmit.length} propuesta(s) enviadas al CISO para revisión.` });
    } catch (e: unknown) {
      setProposalMsg({ type: 'error', text: `Error enviando propuestas: ${e instanceof Error ? e.message : String(e)}` });
    }
  }

  function addLocalProposal() {
    if (!newProposalTitle.trim()) return;
    setLocalProposals(prev => [...prev, {
      title:       newProposalTitle.trim(),
      description: '',
      priority:    'medium',
    }]);
    setNewProposalTitle('');
  }

  function removeLocalProposal(idx: number) {
    setLocalProposals(prev => prev.filter((_, i) => i !== idx));
  }

  if (showMinutes && minutes) {
    return (
      <ACSMinutesPanel
        minutes={minutes}
        localProposals={localProposals}
        onSubmitProposals={handleSubmitProposals}
        onClose={handleClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{activeSession.title}</p>
            <p className="text-xs text-gray-400 truncate">{activeSession.contextLabel}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-4">
          {activeSession.aiMode === 'client' && (
            <span className="text-[10px] bg-yellow-900/40 text-yellow-400 border border-yellow-700 rounded px-2 py-0.5">
              IA cliente
            </span>
          )}
          <button
            onClick={handleEndSession}
            disabled={isEnding || isGenerating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg disabled:opacity-50"
          >
            {(isEnding || isGenerating) ? (
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <StopCircle className="w-3.5 h-3.5" />
            )}
            {isGenerating ? 'Generando minuta...' : 'Terminar sesión'}
          </button>
          <button onClick={handleClose} className="p-1.5 hover:bg-gray-700 rounded-lg text-gray-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body: chat full-width */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat + Propuestas */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {([
              { id: 'chat',      label: 'Chat IA',     Icon: Send     },
              { id: 'proposals', label: `Propuestas (${localProposals.length})`, Icon: ListTodo },
            ] as const).map(({ id, label, Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors ${
                  activeTab === id
                    ? 'text-violet-700 dark:text-violet-400 border-b-2 border-violet-600'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Chat IA */}
          {activeTab === 'chat' && (
            <>
              {/* Avatar animado del asistente */}
              <div className="flex flex-col items-center py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 gap-1.5">
                <ACSAvatar
                  isSpeaking={isSpeaking}
                  color={persona.avatarColor}
                  initials={persona.avatarInitials}
                  size={110}
                />
                <p className="text-[11px] font-semibold" style={{ color: persona.avatarColor }}>
                  {persona.assistantName}
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {/* Presentación del asistente — visible solo cuando no hay mensajes previos */}
                {sessionMessages.length === 0 && (
                  <MessageBubble
                    msg={{
                      id:          '__intro__',
                      meetingId:   activeSession.id,
                      role:        'assistant',
                      content:     introText,
                      messageHash: '',
                      createdAt:   activeSession.startedAt ?? activeSession.scheduledAt,
                    }}
                    assistantName={persona.assistantName}
                    avatarColor={persona.avatarColor}
                    avatarInitials={persona.avatarInitials}
                  />
                )}

                {sessionMessages.map(msg => (
                  <MessageBubble
                    key={msg.id}
                    msg={msg}
                    assistantName={persona.assistantName}
                    avatarColor={persona.avatarColor}
                    avatarInitials={persona.avatarInitials}
                  />
                ))}

                {isSending && (
                  <div className="flex justify-start mb-3 gap-2">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold shadow mt-0.5"
                      style={{ backgroundColor: persona.avatarColor }}
                    >
                      {persona.avatarInitials.slice(0, 3) || 'ACS'}
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-xl px-3.5 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {sendError && (
                <p className="px-4 pb-1 text-xs text-red-500">{sendError}</p>
              )}

              <div className="p-3 border-t border-gray-200 dark:border-gray-700">
                {/* Banner modo voz activo */}
                {voiceMode && (
                  <div className="mb-2 flex items-center gap-1.5 text-[10px] text-violet-700 dark:text-violet-400 font-medium">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
                    Modo voz activo — habla y el asistente responderá automáticamente
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    value={inputText}
                    onChange={e => { setInputText(e.target.value); accumulatedTextRef.current = e.target.value; }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                    }}
                    placeholder={voiceMode ? 'Escuchando... (habla para enviar)' : 'Consulta sobre la normativa...'}
                    rows={2}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                  {/* Botón dictado manual (oculto en modo voz) */}
                  {!voiceMode && (
                    <button
                      type="button"
                      onClick={toggleListening}
                      disabled={isSending}
                      title={isListening ? 'Detener grabación' : 'Dictar mensaje'}
                      className={`p-2 rounded-lg flex-shrink-0 transition-colors ${
                        isListening
                          ? 'bg-red-500 text-white animate-pulse'
                          : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
                      }`}
                    >
                      {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </button>
                  )}
                  {/* Botón modo conversación voz↔voz */}
                  <button
                    type="button"
                    onClick={toggleVoiceMode}
                    disabled={isSending && !voiceMode}
                    title={voiceMode ? 'Desactivar modo voz' : 'Activar conversación voz↔voz'}
                    className={`p-2 rounded-lg flex-shrink-0 transition-colors ${
                      voiceMode
                        ? 'bg-violet-600 text-white animate-pulse'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
                    }`}
                  >
                    {voiceMode ? <PhoneOff className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputText.trim() || isSending}
                    className="p-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50 flex-shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Propuestas */}
          {activeTab === 'proposals' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {localProposals.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">
                    Agrega propuestas de tareas surgidas durante la sesión.<br />
                    El CISO revisará y aprobará antes de crear las tareas.
                  </p>
                )}
                {localProposals.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <CheckCircle className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{p.title}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        p.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        p.priority === 'high'     ? 'bg-orange-100 text-orange-700' :
                        p.priority === 'medium'   ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-600'
                      }`}>
                        {p.priority}
                      </span>
                    </div>
                    <button
                      onClick={() => removeLocalProposal(i)}
                      className="p-0.5 hover:text-red-500 text-gray-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Feedback de envío */}
              {proposalMsg && (
                <div className={`mx-3 mb-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${
                  proposalMsg.type === 'ok'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                }`}>
                  {proposalMsg.type === 'ok'
                    ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                    : <AlertCircle  className="w-3.5 h-3.5 flex-shrink-0" />
                  }
                  {proposalMsg.text}
                </div>
              )}

              {/* Agregar propuesta */}
              <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <div className="flex gap-2">
                  <input
                    value={newProposalTitle}
                    onChange={e => setNewProposalTitle(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addLocalProposal()}
                    placeholder="Nueva propuesta de tarea..."
                    className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  />
                  <button
                    onClick={addLocalProposal}
                    disabled={!newProposalTitle.trim()}
                    className="p-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                {localProposals.length > 0 && (
                  <button
                    onClick={() => handleSubmitProposals(localProposals)}
                    className="w-full py-2 text-sm bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium"
                  >
                    <Paperclip className="w-4 h-4 inline mr-1.5" />
                    Enviar {localProposals.length} propuesta(s) al CISO
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
